import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import shallow from 'zustand/shallow';

import { clevertapOrderDone } from 'lib/analytics/securities';
import { trackAutoBuyOrderDone } from 'lib/analytics/autoOrder';
import If from 'global/ErrorHandlers/If';
import { ORDER_TYPE, STOCK_TYPE } from 'constants/trading';
import useGetPortfolioData from 'hooks/useGetPortfolioData';
import useTradingAccount from 'hooks/useTradingAccount';

import tradingDataStore from 'lib/stores/tradingData';
import { useFliptBoolean } from 'lib/flipt/hooks';
import { FeatureFlagKeys } from 'lib/flipt/constants/flagKeys';
import orderTableStore from 'features/trade/components/OrderTable/store';
import { AutoOrderProduct } from 'global/Securities/types/autoOrder';
import TradingLimitTncModal from 'global/Securities/components/Modal/TradingLimitTncModal';
import {
  DayTradeOnboarding,
  DayTradeTnc,
  DayTradeConfirmation,
  DayTradeUnavailable,
} from 'features/day-trade/components/Modals';
import useUIConfigStore from 'stores/useUIConfigStore';
import {
  BuyOrderType,
  PaymentMethod,
} from 'global/Securities/types/transaction';
import SplitOrderInfoModal from 'global/Securities/components/Modal/SplitOrderInfoModal';
import { EmittenInfoResponse } from 'global/Securities/types/api/emittenInfo';
import useTermsAndConditionStore from 'global/Securities/stores/useTermsAndConditionStore';
import {
  TnCFeatureIDEnum,
  TnCFeaturesIDEnum,
  SubAccountType,
} from 'global/Securities/types/termsAndCondition';

import { useTradingInfoStore } from 'global/Securities/stores/useTradingInfoStore';
import { TradingFeatureEnum } from 'global/Securities/types/trading';
import { SPLIT_ORDER_MODAL_CONTENT } from 'global/Securities/components/Modal/SplitOrderInfoModal/constants';
import useTransactionFormStore from 'global/Securities/stores/useTransactionFormStore';
import { TransactionFormType } from 'typings/transactionForm';
import useGlobalModalStore from 'global/GlobalModalManager/stores/useGlobalModalStore';
import { GlobalModalType } from 'global/GlobalModalManager/types';
import useFastOrderForm from 'features/fast-order/stores/useFastOrderStore';
import useSyariah from 'global/TradingModal/hooks/useSyariah';

import OrderTypeModal from './TransactionModal/OrderTypeModal';
import PreviewOrderModal from './PreviewOrderModal';
import ExchangeFeeModal from './PreviewOrderModal/ExchangeFeeModal';
import OrderPlacedModal from '../OrderPlacedModal';
import AnnouncementModal from './AnnouncementModal';
import TransactionModal from './TransactionModal';
import AutoOrderTncModal from './TransactionModal/AutoOrderTncModal';
import SpecialMonitoringModal from './TransactionModal/SpecialMonitoringModal';
import ShariaUnavailableModal from './ShariaUnavailableModal';

import { MODAL_TYPE } from './types';
import tradingModalStore from '../store';
import realTradingStore from './store';
import { getInitialPrice } from './utils';
import { useBondsModalStore } from './ModalBonds/stores/useBondsModalStore';
import useBondsAnalytics from './ModalBonds/useBondsAnalytics';

type RealTradingModalProps = {
  closeModal: () => void;
  // eslint-disable-next-line react/require-default-props
  onSwitchTab?: (prop: string) => void;
};

function RealTradingModal({ onSwitchTab, closeModal }: RealTradingModalProps) {
  const router = useRouter();
  const isFREligible = useFliptBoolean(FeatureFlagKeys.FR_BONDS);
  const { isRealTrading } = useTradingAccount();
  const [modal, setModal] = useState<MODAL_TYPE | null>(null);
  const setUIValue = useUIConfigStore((s) => s.setUIValue);
  const getUIValue = useUIConfigStore((s) => s.getUIValue);
  const loadTradingInfo = useTradingInfoStore((s) => s.loadTradingInfo);
  const openBuyBonds = useBondsModalStore((s) => s.openBuyModal);
  const { FRBondActionTrackers } = useBondsAnalytics();

  const [formType, setFormType] = useTransactionFormStore((s) => [
    s.formType,
    s.setFormType,
  ]);
  const [activeModal, setActiveModal] = useGlobalModalStore((s) => [
    s.activeModal,
    s.setActiveModal,
  ]);
  const setFastOrderSymbol = useFastOrderForm((s) => s.setSymbol);

  const [
    symbol,
    orderType,
    priceProps,
    orderedPrice,
    modalData,
    onClose,
    showTradingModal,
    showTabs,
    showOrderbook,
    setModalState,
  ] = tradingModalStore(
    (s) => [
      s.symbol,
      s.orderType,
      s.price,
      s.orderedPrice,
      s.modalData,
      s.onClose,
      s.showModal,
      s.showTabs,
      s.showOrderbook,
      s.setModalState,
    ],
    shallow,
  );

  const [
    checkRightTradeable,
    getExercise,
    getCompany,
    getOrderHistory,
    getPortfolio,
    getConfigFormula,
    resetModalData,
    resetCompany,
  ] = tradingDataStore(
    (s) => [
      s.checkRightTradeable,
      s.getExercise,
      s.getCompany,
      s.getOrderHistory,
      s.getPortfolio,
      s.getConfigFormula,
      s.resetModalData,
      s.resetCompany,
    ],
    shallow,
  );

  const isBuy = orderType === ORDER_TYPE.BUY;
  const isSell = orderType === ORDER_TYPE.SELL;

  const setOrderTab = orderTableStore((s) => s.setOrderTab);
  const [setPrice, setStockCode, buyOrderType, dayTradeActive] =
    realTradingStore(
      (s) => [s.setPrice, s.setStockCode, s.type, s.dayTradeActive],
      shallow,
    );

  const portfolioData = useGetPortfolioData(
    symbol,
    true,
    false,
    dayTradeActive,
  );

  const loadTnC = useTermsAndConditionStore((s) => s.loadTnC);
  const termsData = useTermsAndConditionStore((s) => s.termsData);
  const loadTnCAcceptance = useTermsAndConditionStore(
    (s) => s.loadTnCAcceptance,
  );
  const loadSubAccountStatus = useTermsAndConditionStore(
    (s) => s.loadSubAccountStatus,
  );

  const {
    isUserSyariah,
    checkSyariahTradeableStatus,
    hideSyariahRestrictionModal,
  } = useSyariah();

  const loadCompanyData = async (): Promise<EmittenInfoResponse> => {
    const { data } = await getCompany(symbol);
    return data as EmittenInfoResponse;
  };

  const loadRightTradeableStatus = async (): Promise<boolean> => {
    const { data } = await checkRightTradeable();
    return data?.is_tradeable;
  };

  const loadExercisableData = async (): Promise<{
    isExercisable: boolean;
    isExerciseTradeable: boolean;
  }> => {
    const { data } = await getExercise(symbol);
    return {
      isExercisable: data?.is_exerciseable,
      isExerciseTradeable: data?.is_tradeable,
    };
  };

  const getTradeableStatus = async (
    emittenInfo: EmittenInfoResponse,
  ): Promise<boolean> => {
    const { type_company: type } = emittenInfo;
    const isRight = type.toLowerCase() === STOCK_TYPE.right;
    const isWarrant = type.toLowerCase() === STOCK_TYPE.waran;

    if (isRight && isSell) {
      const isRightTradeable = await loadRightTradeableStatus();
      return isRightTradeable;
    }

    if (![isRight, isWarrant].includes(true) || !isBuy) return true;
    if (isRight) {
      const isRightTradeable = await loadRightTradeableStatus();
      if (!isRightTradeable) return false;
    }

    const { isExerciseTradeable } = await loadExercisableData();
    return isExerciseTradeable;
  };

  const getTransactionStatus = async (
    emittenInfo: EmittenInfoResponse,
  ): Promise<boolean> => {
    if (orderType === ORDER_TYPE.EXERCISE) {
      return (await loadExercisableData()).isExercisable;
    }

    return getTradeableStatus(emittenInfo);
  };

  const setInitialPrice = (emittenInfo: EmittenInfoResponse) => {
    const { tradeable } = emittenInfo;

    if (tradeable) {
      setPrice(
        priceProps || getInitialPrice(emittenInfo, orderType, orderedPrice),
      );
      setStockCode(symbol);
    }
  };

  const handleResetData = (isPreventCloseModal = false) => {
    resetCompany();
    resetModalData();
    setModal(null);
    if (!isPreventCloseModal) closeModal();
  };

  const handleSubmitOrderPlaced = async () => {
    setModal(MODAL_TYPE.TRANSACTION);
    clevertapOrderDone({
      orderType,
    });

    if (buyOrderType === BuyOrderType.AutoBuy) trackAutoBuyOrderDone();
    handleResetData();

    if (orderType === ORDER_TYPE.EXERCISE) setOrderTab('exercise');

    if (['/securities/order/', '/securities/order'].includes(router?.asPath)) {
      getPortfolio(isRealTrading);
      getOrderHistory(true);
    } else router.push('/securities/order');
  };

  const handleShowSplitOrderInfo = () => {
    setModal(MODAL_TYPE.SPLIT_ORDER_INFO);
  };

  const handleShowSplitMethodInfo = () => {
    setModal(MODAL_TYPE.SPLIT_ORDER_METHOD_INFO);
  };

  const handleShowTradingLimitTnc = () => {
    loadTnC(
      TnCFeatureIDEnum.FEATURE_TRADING_LIMIT,
      /**
       * If user is not yet accepted the TnC,
       * then show the modal using the callback
       */
      () => {
        setUIValue('tradingLimitTnc', false);
        setUIValue('transactionPaymentMethod', PaymentMethod.TradingBalance);
        setModal(MODAL_TYPE.TRADING_LIMIT_TNC);
      },
      /**
       * If user was accepted the TnC before,
       * then show continue their action to use the Trading limit as payment
       */
      () => {
        setUIValue('tradingLimitTnc', true);
        setUIValue('transactionPaymentMethod', PaymentMethod.TradingLimit);
        setModal(MODAL_TYPE.TRANSACTION);
      },
    );
  };

  const handleTradingLimitTncSubmit = () => {
    setModal(MODAL_TYPE.TRANSACTION);
  };

  const loadDayTradeTncStatus = async () =>
    loadTnCAcceptance([
      TnCFeaturesIDEnum.TRADING_LIMIT,
      TnCFeaturesIDEnum.DAY_TRADE,
    ]);

  const loadDayTradeSubAccountStatus = async () => {
    const subAccountActive = getUIValue('dayTradeSubAccountActive');
    if (subAccountActive) return subAccountActive;

    return loadSubAccountStatus(SubAccountType.DAY_TRADE);
  };

  const getDayTradeTncStatus = async (checkShown = true) => {
    if (isUserSyariah) return true;

    if (!termsData) await loadDayTradeTncStatus();

    const isActive = await loadDayTradeSubAccountStatus();
    if (isActive) return true;

    const dayTradeTncShown = getUIValue('dayTradeTncShown');
    if (dayTradeTncShown !== undefined && checkShown) return dayTradeTncShown;

    const dayTradeTnc = getUIValue('dayTradeTnc');
    if (dayTradeTnc !== undefined) return dayTradeTnc;

    return loadDayTradeTncStatus();
  };

  const handleOpenModalDayTrade = async () => {
    const isDayTradeAccepted = await getDayTradeTncStatus(false);
    if (!isDayTradeAccepted) {
      setModal(MODAL_TYPE.DAY_TRADE_ONBOARDING);
    }
  };

  const handleCloseModalDayTrade = () => {
    const { showDayTradeOnboarding } = modalData || {};
    if (showDayTradeOnboarding && !symbol && !orderType) {
      setModal(null);
      setModalState({
        modalData: {
          showDayTradeOnboarding: false,
        },
      });
    } else {
      setModal(MODAL_TYPE.TRANSACTION);
    }

    setUIValue('dayTradeTncShown', true);
  };

  const getShariaTradableStatus = async () => {
    if (!isUserSyariah || !isBuy) return true;

    const isShariaTradable = await checkSyariahTradeableStatus(symbol);
    if (!isShariaTradable) {
      setModal(MODAL_TYPE.SHARIA_UNAVAILABLE);
      return false;
    }

    return true;
  };

  const handleCloseModalSharia = () => {
    setModal(null);
    hideSyariahRestrictionModal();
    handleResetData();
  };

  const handlePlaceOrder = async () => {
    if (!(await getShariaTradableStatus())) return;

    setModal(MODAL_TYPE.PREVIEW);
  };

  const handleLoadInitialData = useCallback(async () => {
    try {
      if (!(await getShariaTradableStatus())) return;

      if (!symbol || modalData?.openFromWidget) return;

      const isSwitchToClassic = [
        ORDER_TYPE.AMEND_BUY,
        ORDER_TYPE.AMEND_SELL,
        ORDER_TYPE.EXERCISE,
      ].includes(orderType);

      if (formType === TransactionFormType.FastOrder) {
        if (!isSwitchToClassic) return;
        setFormType(TransactionFormType.Classic);
      }

      await getConfigFormula();
      const companyData = await loadCompanyData();

      const isTransactionable = await getTransactionStatus(
        companyData as EmittenInfoResponse,
      );

      if (!isTransactionable) {
        setModal(MODAL_TYPE.ANNOUNCEMENT);
        return;
      }

      // load trading information
      await loadTradingInfo([
        TradingFeatureEnum.SPLIT_ORDER,
        // add more trading feature using the TradingFeatureEnum
      ]);

      // Will only fetch portfolio list on page that has not fetch the API
      if (!router.asPath.includes('portfolio')) await getPortfolio(true);

      setInitialPrice(companyData as EmittenInfoResponse);

      // Check if user has accepted the day trade TnC
      if (!(await getDayTradeTncStatus())) {
        setModal(MODAL_TYPE.DAY_TRADE_ONBOARDING);
        return;
      }

      setModal(MODAL_TYPE.TRANSACTION);
    } catch (e) {
      handleResetData();
    }
  }, [symbol]);

  // Handle show modal from outside transaction
  const handleLoadRestriction = useCallback(async () => {
    if (!modalData || modalData?.openFromWidget) return;

    // Handle show syariah restriction modal
    const { showShariaRestrictionBuy } = modalData || {};
    if (isUserSyariah) {
      if (showShariaRestrictionBuy) {
        setModal(MODAL_TYPE.SHARIA_UNAVAILABLE);
        return;
      }

      setModalState({
        modalData: {
          showShariaRestrictionBuy: false,
        },
      });
    }

    // Handle show day trade onboarding modal
    const { showDayTradeOnboarding } = modalData || {};
    if (showDayTradeOnboarding) {
      if (!(await getDayTradeTncStatus())) {
        setModal(MODAL_TYPE.DAY_TRADE_ONBOARDING);
        return;
      }

      setModalState({
        modalData: {
          showDayTradeOnboarding: false,
        },
      });
    }
  }, [
    isUserSyariah,
    modalData?.showDayTradeOnboarding,
    modalData?.showShariaRestrictionBuy,
    modalData?.openFromWidget,
  ]);

  useEffect(() => {
    handleLoadInitialData();
    return () => {
      handleResetData(true);
    };
  }, [handleLoadInitialData]);

  useEffect(() => {
    handleLoadRestriction();
  }, [handleLoadRestriction]);

  // Redirect to Fast Order modal and closing classic modal
  // This due to too many tradingModalStore.showModal reference to be refactored
  useEffect(() => {
    const isSwitchToClassic = [
      ORDER_TYPE.AMEND_BUY,
      ORDER_TYPE.AMEND_SELL,
      ORDER_TYPE.EXERCISE,
    ].includes(orderType);
    const isFastOrder =
      formType === TransactionFormType.FastOrder &&
      activeModal !== GlobalModalType.FastOrder;

    const isShouldOpenFastOrder = symbol && isFastOrder && !isSwitchToClassic;

    if (isShouldOpenFastOrder) {
      setFastOrderSymbol(symbol);
      setActiveModal(GlobalModalType.FastOrder, {
        switchFormConfig: {
          orderType,
          showTabs,
          showOrderbook,
        },
      });
      handleResetData();
      return;
    }

    // Switch back to classic form if condition to open fast order not met
    setFormType(TransactionFormType.Classic);
  }, [symbol, formType, orderType, showTabs]);

  const getTransactionModalVisible = useCallback(
    (value: TransactionFormType) =>
      modal === MODAL_TYPE.TRANSACTION && formType === value,
    [formType, modal],
  );

  const isClassicFormVisible = useMemo(
    () => getTransactionModalVisible(TransactionFormType.Classic),
    [getTransactionModalVisible],
  );

  if (!modal) return <></>;

  return (
    <>
      <If condition={modal === MODAL_TYPE.ANNOUNCEMENT}>
        <AnnouncementModal
          visible={modal === MODAL_TYPE.ANNOUNCEMENT}
          orderType={orderType}
          closeModal={handleResetData}
          onSubmit={onClose}
          showTransactionModal={() => setModal(MODAL_TYPE.TRANSACTION)}
        />
      </If>

      <If condition={isClassicFormVisible}>
        <TransactionModal
          visible={isClassicFormVisible}
          showAutoBuyTncModal={() => setModal(MODAL_TYPE.AUTOBUY_TNC)}
          showOrderTypeModal={() => setModal(MODAL_TYPE.ORDER_TYPE)}
          showSpecialMonitoringModal={() =>
            setModal(MODAL_TYPE.SPECIAL_MONITORING)
          }
          onUpdateSymbol={(newSymbol: string, isBonds?: boolean) => {
            if (isBonds && isFREligible) {
              openBuyBonds(newSymbol);
              FRBondActionTrackers.onOrderButtonClicked(
                'Buy',
                newSymbol,
                'Search Company Page',
              );
              onClose();
              return;
            }
            showTradingModal({
              symbol: newSymbol,
              orderType,
              modalData: {
                openFromSearch: true,
              },
            });
          }}
          onShowTradingLimitTnc={handleShowTradingLimitTnc}
          onShowSplitOrderInfo={handleShowSplitOrderInfo}
          onShowSplitOrderMethodInfo={handleShowSplitMethodInfo}
          onSubmit={handlePlaceOrder}
          closeModal={handleResetData}
          onSwitchTab={onSwitchTab}
          portfolioData={portfolioData}
          onShowDayTradeOnboarding={handleOpenModalDayTrade}
          onShowDayTradeUnavailable={() =>
            setModal(MODAL_TYPE.DAY_TRADE_UNAVAILABLE)
          }
        />
      </If>

      <If condition={modal === MODAL_TYPE.ORDER_TYPE}>
        <OrderTypeModal
          visible={modal === MODAL_TYPE.ORDER_TYPE}
          closeModal={() => setModal(MODAL_TYPE.TRANSACTION)}
        />
      </If>

      <If condition={modal === MODAL_TYPE.SPECIAL_MONITORING}>
        <SpecialMonitoringModal
          visible={modal === MODAL_TYPE.SPECIAL_MONITORING}
          closeModal={() => setModal(MODAL_TYPE.TRANSACTION)}
        />
      </If>

      <If condition={modal === MODAL_TYPE.PREVIEW}>
        <PreviewOrderModal
          visible={modal === MODAL_TYPE.PREVIEW}
          showExchangeFee={() => setModal(MODAL_TYPE.EXCHANGE_FEE)}
          onCancel={() => setModal(MODAL_TYPE.TRANSACTION)}
          onSubmit={() => setModal(MODAL_TYPE.ORDER_PLACED)}
          portfolioData={portfolioData}
        />
      </If>

      <If condition={modal === MODAL_TYPE.EXCHANGE_FEE}>
        <ExchangeFeeModal
          visible={modal === MODAL_TYPE.EXCHANGE_FEE}
          closeModal={() => setModal(MODAL_TYPE.PREVIEW)}
          orderType={orderType}
        />
      </If>

      <If condition={modal === MODAL_TYPE.ORDER_PLACED}>
        <OrderPlacedModal
          visible={modal === MODAL_TYPE.ORDER_PLACED}
          onSubmit={handleSubmitOrderPlaced}
          orderType={orderType}
        />
      </If>

      <If condition={modal === MODAL_TYPE.AUTOBUY_TNC}>
        <AutoOrderTncModal
          autoOrderType={AutoOrderProduct.AutoBuy}
          visible={modal === MODAL_TYPE.AUTOBUY_TNC}
          closeModal={() => setModal(MODAL_TYPE.TRANSACTION)}
        />
      </If>

      <If condition={modal === MODAL_TYPE.TRADING_LIMIT_TNC}>
        <TradingLimitTncModal
          visible={modal === MODAL_TYPE.TRADING_LIMIT_TNC}
          onCancel={() => setModal(MODAL_TYPE.TRANSACTION)}
          onSubmit={handleTradingLimitTncSubmit}
        />
      </If>

      <If condition={modal === MODAL_TYPE.SPLIT_ORDER_INFO}>
        <SplitOrderInfoModal
          visible={modal === MODAL_TYPE.SPLIT_ORDER_INFO}
          onCancel={() => setModal(MODAL_TYPE.TRANSACTION)}
        />
      </If>

      <If condition={modal === MODAL_TYPE.DAY_TRADE_ONBOARDING}>
        <DayTradeOnboarding
          visible={modal === MODAL_TYPE.DAY_TRADE_ONBOARDING}
          maskClosable={false}
          onCancel={handleCloseModalDayTrade}
          onSubmit={() => setModal(MODAL_TYPE.DAY_TRADE_TNC)}
        />
      </If>

      <If condition={modal === MODAL_TYPE.DAY_TRADE_TNC}>
        <DayTradeTnc
          visible={modal === MODAL_TYPE.DAY_TRADE_TNC}
          maskClosable={false}
          onCancel={handleCloseModalDayTrade}
          onSubmit={() => setModal(MODAL_TYPE.DAY_TRADE_CONFIRMATION)}
        />
      </If>

      <If condition={modal === MODAL_TYPE.DAY_TRADE_CONFIRMATION}>
        <DayTradeConfirmation
          visible={modal === MODAL_TYPE.DAY_TRADE_CONFIRMATION}
          maskClosable={false}
          onCancel={handleCloseModalDayTrade}
          onSubmit={handleCloseModalDayTrade}
        />
      </If>

      <If condition={modal === MODAL_TYPE.DAY_TRADE_UNAVAILABLE}>
        <DayTradeUnavailable
          visible={modal === MODAL_TYPE.DAY_TRADE_UNAVAILABLE}
          onCancel={() => setModal(MODAL_TYPE.TRANSACTION)}
          onSubmit={() => setModal(MODAL_TYPE.TRANSACTION)}
        />
      </If>

      <If condition={modal === MODAL_TYPE.SPLIT_ORDER_METHOD_INFO}>
        <SplitOrderInfoModal
          title="Split Method"
          description=""
          info={SPLIT_ORDER_MODAL_CONTENT.SPLIT_METHOD}
          visible={modal === MODAL_TYPE.SPLIT_ORDER_METHOD_INFO}
          onCancel={() => setModal(MODAL_TYPE.TRANSACTION)}
        />
      </If>

      <If condition={modal === MODAL_TYPE.SHARIA_UNAVAILABLE}>
        <ShariaUnavailableModal
          visible={modal === MODAL_TYPE.SHARIA_UNAVAILABLE}
          onClose={handleCloseModalSharia}
        />
      </If>
    </>
  );
}

export default RealTradingModal;
